import { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import * as adminApi from '../../api/admin.api';
import type { AdminReport } from '../../types/admin';
import styles from './AdminReports.module.css';

const statusVariant: Record<string, 'success' | 'warning' | 'info' | 'neutral'> = {
  new: 'info',
  in_progress: 'warning',
  resolved: 'success',
  answered: 'warning',
  reopened: 'info',
};

const statusLabel: Record<string, string> = {
  new: 'Новое',
  in_progress: 'В работе',
  resolved: 'Решено',
  answered: 'Ответ получен',
  reopened: 'Возвращено',
};

const categoryLabel: Record<string, string> = {
  connection: 'Ошибка подключения',
  speed: 'Низкая скорость',
  bug: 'Не работает сервис',
  other: 'Другое',
};

export function AdminReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchReports = async () => {
    try {
      const data = await adminApi.getReports();
      setReports(data);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleExpand = (report: AdminReport) => {
    if (expandedId === report.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(report.id);
    setResponseText(report.adminResponse || '');
  };

  const handleRespond = async (report: AdminReport) => {
    setSaving(true);
    try {
      await adminApi.respondToReport(report.id, responseText);
      await fetchReports();
      setExpandedId(null);
    } catch {
      // Silently ignore
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (user?.role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>Доступ запрещён</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Обращения</h1>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : reports.length === 0 ? (
        <div className={styles.emptyState}>Обращений не найдено</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Пользователь</th>
                <th>Категория</th>
                <th>Статус</th>
                <th>Дата</th>
                <th>Сообщение</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <Fragment key={r.id}>
                  <tr
                    className={styles.expandableRow}
                    onClick={() => handleExpand(r)}
                  >
                    <td>
                      <span className={styles.ticketNumber}>{r.number}</span>
                    </td>
                    <td>{r.userEmail}</td>
                    <td>{categoryLabel[r.category] || r.category}</td>
                    <td>
                      <Badge variant={statusVariant[r.status] || 'neutral'}>
                        {statusLabel[r.status] || r.status}
                      </Badge>
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                    <td>
                      <span className={styles.message}>{r.message}</span>
                    </td>
                  </tr>
                  {expandedId === r.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <div className={styles.expandedPanel}>
                          <div className={styles.expandedLabel}>Полное сообщение</div>
                          <div className={styles.expandedMessage}>{r.message}</div>

                          {r.userComment && (
                            <div className={styles.userComment}>
                              <div className={styles.userCommentLabel}>
                                Комментарий пользователя:
                              </div>
                              <div className={styles.userCommentText}>
                                {r.userComment}
                              </div>
                            </div>
                          )}

                          {r.adminResponse && (
                            <div className={styles.existingResponse}>
                              <div className={styles.existingResponseLabel}>
                                Предыдущий ответ
                              </div>
                              <div className={styles.existingResponseText}>
                                {r.adminResponse}
                              </div>
                            </div>
                          )}

                          <div className={styles.expandedLabel}>Ответ</div>
                          <textarea
                            className={styles.responseArea}
                            placeholder="Введите ответ..."
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                          />

                          <div className={styles.expandedActions}>
                            <Button
                              variant="primary"
                              size="sm"
                              loading={saving}
                              onClick={() => handleRespond(r)}
                              disabled={!responseText.trim()}
                            >
                              Ответить
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setExpandedId(null)}
                            >
                              Отмена
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
